import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useRef } from "react";
import { Orb } from "./Orb";
import { NEXT_ASSESSMENT_URL, WHATSAPP_URL } from "../constants";

const Line = ({ children, delay, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      <div className="absolute aspect-square -translate-y-1/2 right-[-14%] top-[38%] w-[44vw] sm:right-[-12%] sm:top-[8%] sm:w-[35vw] lg:right-[-3%] lg:top-[48%] lg:w-[42vw]">
        <motion.div style={{ y: orbY, opacity: fade }} className="hidden md:block w-full h-full">
          <Orb />
        </motion.div>
      </div>

      <div className="absolute inset-0 pointer-events-none hidden md:block">
        <div className="absolute top-[15%] left-[55%] w-72 h-72 rounded-full bg-[#FE7815]/10 blur-3xl" />
        <div className="absolute bottom-[10%] left-[5%] w-96 h-96 rounded-full bg-[#F01E1F]/5 blur-3xl" />
      </div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full"
      >
        <div className="max-w-3xl">
          <Line delay={0.3}>
            <span
              data-testid="hero-overline"
              className="inline-flex items-center gap-3 text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-[#FE7815] mb-8"
            >
              <span className="w-10 h-[2px] bg-gradient-to-r from-[#FE7815] to-[#F01E1F]" />
              Study abroad mentoring
            </span>
          </Line>

          <h1
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#111111]"
          >
            <Line delay={0.45}>Your dreams deserve</Line>
            <Line delay={0.58}>
              <span className="text-gradient">a bigger world.</span>
            </Line>
          </h1>

          <Line delay={0.9}>
            <p
              data-testid="hero-subtitle"
              className="mt-8 text-base sm:text-lg text-[#111111] leading-relaxed max-w-xl"
            >
              Studying abroad is about who you become. Whether you dream of
              deepening your knowledge or building your career, we understand
              your ambitions and help you find the right education—and a world
              of new perspectives.
            </p>
          </Line>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group inline-flex items-center justify-center gap-2.5 text-base font-bold text-white px-8 py-4 rounded-full bg-gradient-to-r from-[#FE7815] to-[#F01E1F] hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/30 active:translate-y-0 transition-[transform,box-shadow] duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Plan Your Study Abroad
            </a>
            <a
              href={NEXT_ASSESSMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-next-assessment-button"
              className="group inline-flex items-center justify-center gap-2 text-base font-bold text-[#111111] px-8 py-4 rounded-full border-2 border-[#111111]/12 hover:border-[#FE7815] hover:text-[#FE7815] transition-colors duration-300"
            >
              Explore Your Career Direction
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-stats-ribbon"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-16 flex items-center gap-8 sm:gap-12"
          >
            {[
              
              { n: "2012", l: "The Beginning of Our Journey" },
              { n: "2017", l: "The Beginning of Movementor" },
              { n: "01", l: " Our Focus: The Student" },
            ].map((s, i) => (
              <div key={i} className="flex flex-col" data-testid={`hero-stat-${i}`}>
                <span className="text-2xl sm:text-3xl font-black text-[#111111]">
                  {s.n}
                </span>
                <span className="text-xs sm:text-sm text-[#111111] mt-1">
                  {s.l}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#111111]">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-[2px] h-10 bg-gradient-to-b from-[#FE7815] to-transparent"
        />
      </motion.div>
    </section>
  );
};
